import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';

const POLL_INTERVAL_MS = 1500;
// ~30s of polling before giving up — long enough to cover normal worker
// scheduling latency, short enough that a recruiter isn't staring at a
// spinner (or generating ~40 requests/minute) indefinitely.
const MAX_POLL_ATTEMPTS = 20;

interface SourcingAgentResponse {
  agent: { id: number; state?: string };
}

interface CurrentRoundResponse {
  round: { id: number; status: string; roundNumber: number };
}

type PageState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'no-matches' };

/**
 * US-404 (Epic 49 Phase 3, Task 7). The real destination behind the
 * TuningBanner's "Tune these suggestions" link (`/jobs/:id/sourcing/tune`).
 *
 * This page has no UI of its own to speak of — it exists purely to make the
 * calibration flow reachable without already knowing two database ids.
 * Phase 1 shipped only the deep-link calibration page at
 * `/sourcing-agents/:agentId/calibration/:roundId`; a banner linking here
 * without this redirect step would reproduce that exact unreachability
 * problem.
 *
 * Sequence:
 * 1. POST /api/jobs/:id/sourcing-agent — creates the agent (moves it to
 *    'awaiting_calibration', enqueues the first calibration round).
 * 2. If that 409s (AgentAlreadyExistsError — a second click, or the agent was
 *    created by another route), fall back to GET /api/jobs/:id/sourcing-agent
 *    to resolve the existing agent id. Either path converges on the same
 *    agent id, so this is idempotent rather than an error on repeat visits.
 * 3. Poll GET /api/sourcing-agents/:agentId/current-round until the round
 *    row exists (the worker inserts it asynchronously once its BullMQ job
 *    runs — it is never available synchronously from step 1/2's response),
 *    then redirect into the calibration UI.
 *
 * US-404 fix round: this polling loop used to retry forever on ANY error with
 * no cap. That is the MOST LIKELY outcome for a real recruiter, not an edge
 * case — `process-calibration-round.ts` deliberately creates no round row at
 * all when the candidate pool is empty (its own comment: an empty pool is
 * "today's REAL production default" because `PASSIVE_SOURCING_ENABLED` has
 * never been set in prod), and `passiveSourcingEnabled` is a per-job DB flag
 * independent of that env switch — so a job can show the tuning banner while
 * `similarity_scores` is empty for it. Same dead-end for a failed enqueue
 * (caught and logged server-side, agent left in `awaiting_calibration`
 * forever) or a genuine 401/403/500. Now: only a 404 (round genuinely doesn't
 * exist yet) is retried, capped at MAX_POLL_ATTEMPTS, with an actionable
 * give-up message rather than an endless spinner.
 */
export default function SourcingTuneEntry() {
  const { id } = useParams<{ id: string }>();
  const jobId = Number(id);
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    function genericErrorMessage(err: unknown): string {
      return err instanceof Error ? err.message : 'Something went wrong setting up tuning for this role.';
    }

    async function resolveAgentId(): Promise<number> {
      try {
        const created = await api.post<SourcingAgentResponse>(`/api/jobs/${jobId}/sourcing-agent`);
        return created.agent.id;
      } catch (err) {
        const statusCode = (err as { statusCode?: number } | undefined)?.statusCode;
        if (statusCode === 409) {
          const existing = await api.get<SourcingAgentResponse>(`/api/jobs/${jobId}/sourcing-agent`);
          return existing.agent.id;
        }
        throw err;
      }
    }

    function pollForRound(agentId: number, attempt: number): void {
      if (cancelled) return;
      api
        .get<CurrentRoundResponse>(`/api/sourcing-agents/${agentId}/current-round`)
        .then(({ round }) => {
          if (cancelled) return;
          navigate(`/sourcing-agents/${agentId}/calibration/${round.id}`, { replace: true });
        })
        .catch((err) => {
          if (cancelled) return;
          const statusCode = (err as { statusCode?: number } | undefined)?.statusCode;
          // Only a 404 ("no round yet") is worth retrying. Anything else —
          // 401/403/500, a network error — will never resolve by waiting, so
          // it must stop immediately instead of retrying into a silent loop.
          if (statusCode !== 404) {
            setState({ kind: 'error', message: genericErrorMessage(err) });
            return;
          }
          if (attempt >= MAX_POLL_ATTEMPTS) {
            // C2 (2026-08-04). Before declaring "no matches", ask once more
            // including FAILED rounds. `current-round` hides those by default
            // so a retry can't bounce back into the failure it just left —
            // but that also means a recruiter who closed the tab while Arbie
            // screened would be told there are no candidate matches, when in
            // truth there is a failed round with a reason and a retry button
            // waiting at a URL nothing else in the app links to.
            api
              .get<CurrentRoundResponse>(`/api/sourcing-agents/${agentId}/current-round?includeFailed=true`)
              .then(({ round }) => {
                if (cancelled) return;
                navigate(`/sourcing-agents/${agentId}/calibration/${round.id}`, { replace: true });
              })
              .catch(() => {
                // Genuinely nothing to show — including no failed round.
                if (!cancelled) setState({ kind: 'no-matches' });
              });
            return;
          }
          pollTimer = setTimeout(() => pollForRound(agentId, attempt + 1), POLL_INTERVAL_MS);
        });
    }

    async function run() {
      try {
        const agentId = await resolveAgentId();
        pollForRound(agentId, 1);
      } catch (err) {
        if (!cancelled) setState({ kind: 'error', message: genericErrorMessage(err) });
      }
    }

    if (Number.isInteger(jobId) && jobId > 0) {
      run();
    } else {
      setState({ kind: 'error', message: 'Invalid job.' });
    }

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [jobId, navigate]);

  if (state.kind === 'error') {
    return <div className="p-6 text-sm text-red-500 dark:text-red-400">{state.message}</div>;
  }

  if (state.kind === 'no-matches') {
    return (
      <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
        Arbie doesn't have candidate matches for this role yet. Once nightly sourcing finds some, you'll be able to tune them.
      </div>
    );
  }

  return <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Setting up tuning for this role…</div>;
}
